import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, mediaUrl } from "@/lib/api";
import { btnPrimary, money } from "@/components/simple/product/Fields";
import { useReorder } from "@/components/track/useReorder";

const tel = (p) => (p || "").replace(/[^\d+]/g, "");

/** The five steps (7.9): Order placed → Proof → Printing → Ready / Shipped → Done. */
export function Steps({ step, fulfillment, cancelled }) {
    const labels = ["Order placed", "Proof", "Printing", fulfillment === "ship" ? "Shipped" : "Ready", "Done"];
    if (cancelled) return <p className="border border-ink px-3 py-2 text-[20px] font-semibold" data-testid="track-cancelled">Cancelled</p>;
    return (
        <ol className="grid grid-cols-5 gap-1" data-testid="track-steps" aria-label="Order progress">
            {labels.map((l, i) => {
                const n = i + 1;
                const done = n <= step;
                return (
                    <li key={l} className="flex flex-col gap-1" data-testid={`track-step-${n}`} data-done={done ? "1" : "0"}>
                        <span className={`h-2 border border-ink ${done ? "bg-accent-brand" : "bg-page"}`} />
                        <span className={`text-[12px] leading-tight sm:text-[14px] ${done ? "font-semibold" : "text-muted-ink"}`}>{l}</span>
                    </li>
                );
            })}
        </ol>
    );
}

export function Lines({ items }) {
    return (
        <ul className="border border-ink" data-testid="track-lines">
            {items.map((it, i) => (
                <li key={it.uid || i} className="flex items-center gap-3 border-t border-line px-3 py-2 first:border-t-0" data-testid="track-line">
                    {it.image && (
                        <div className="h-12 w-12 shrink-0 border border-line bg-page">
                            <img src={mediaUrl(it.image)} alt="" className="h-full w-full object-cover" onError={(e) => (e.target.parentElement.style.display = "none")} />
                        </div>
                    )}
                    <div className="min-w-0 flex-1">
                        <p className="truncate text-[16px] font-semibold">{it.product_name}</p>
                        {it.summary && <p className="truncate text-[14px] text-muted-ink">{it.summary}</p>}
                    </div>
                    <p className="text-[16px]">{money(it.line_total)}</p>
                </li>
            ))}
        </ul>
    );
}

/** One order on the tracker: steps, ready-by, lines, the one primary action and Cancel (unpaid only) with a 5-second Undo. */
export default function Tracker({ order, onChange }) {
    const [view, setView] = useState(order);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [undo, setUndo] = useState(0);
    const timer = useRef(null);
    const { reorder, busy: reordering } = useReorder();
    useEffect(() => setView(order), [order]);
    useEffect(() => () => clearInterval(timer.current), []);

    const update = (v) => { setView(v); onChange?.(v); };
    const refresh = async () => {
        const { data } = await api.get(`/orders/track/${view.order_number}`);
        update(data);
    };
    const run = async (fn) => {
        setBusy(true); setError("");
        try { await fn(); } catch (e) { setError(formatApiError(e.response?.data?.detail) || "That didn't go through. Try again."); }
        finally { setBusy(false); }
    };
    const approve = () => run(async () => { await api.post(`/proof/${view.proof_token}/approve`); await refresh(); });
    const cancel = () => run(async () => {
        const { data } = await api.post(`/orders/${view.id}/cancel`, { token: view.reorder_token });
        update(data);
        setUndo(5);
        clearInterval(timer.current);
        timer.current = setInterval(() => setUndo((n) => { if (n <= 1) { clearInterval(timer.current); return 0; } return n - 1; }), 1000);
    });
    const undoCancel = () => run(async () => {
        clearInterval(timer.current); setUndo(0);
        const { data } = await api.post(`/orders/${view.id}/uncancel`, { token: view.reorder_token });
        update(data);
    });

    const payNow = () => run(async () => {
        const origin = window.location.origin;
        const { data } = await api.post("/payments/order/checkout", { order_id: view.id, origin_url: origin, cancel_url: `${origin}/track/${view.order_number}` });
        window.location.href = data.checkout_url;
    });

    const shop = view.shop || {};
    const isPayment = view.order_type && view.order_type !== "print";
    const payLink = `/pay?amount=${view.balance_due}&for=${encodeURIComponent(view.balance_for || "Balance")}&order=${view.order_number}`;
    const primary = view.cancelled ? null
        : view.needs_payment ? { label: `Pay ${money(view.card_total)}`, onClick: payNow, testid: "track-pay-now" }
            : view.proof_waiting ? { label: "Approve proof", onClick: approve, testid: "track-approve" }
                : view.balance_due > 0 ? { label: `Pay balance ${money(view.balance_due)}`, to: payLink, testid: "track-pay-balance" }
                    : !isPayment ? { label: "Reorder", onClick: () => reorder({ id: view.id, token: view.reorder_token }), testid: "track-reorder" } : null;

    return (
        <article className="border border-ink p-3" data-testid={`tracker-${view.order_number}`}>
            <div className="flex items-baseline justify-between gap-3">
                <h2 className="text-[20px] font-semibold" data-testid="track-order-number">{view.order_number}</h2>
                <span className="text-[14px] text-muted-ink">{new Date(view.created_at).toLocaleDateString()}</span>
            </div>
            <div className="mt-3"><Steps step={view.step} fulfillment={view.fulfillment} cancelled={view.cancelled} /></div>
            {!view.cancelled && view.ready_text && (
                <p className="mt-3 text-[20px] font-semibold" data-testid="track-ready-by">{view.step >= 5 ? "Done" : view.step >= 4 ? (view.fulfillment === "ship" ? "Shipped" : "Ready for pickup") : `Ready by ${view.ready_text}`}</p>
            )}
            {view.pickup_code && (
                <p className="mt-2 border border-ink px-3 py-2 text-[16px]" data-testid="track-pickup-code">
                    Locker code: <span className="font-semibold">{view.pickup_code}</span>{shop.pickup_address ? ` · ${shop.pickup_address}` : ""}
                </p>
            )}
            {view.tracking_url && <a href={view.tracking_url} className="mt-2 flex min-h-[44px] items-center text-[16px] underline" data-testid="track-shipping-link" target="_blank" rel="noreferrer">Shipment tracking{view.tracking_number ? ` · ${view.tracking_number}` : ""}</a>}
            {view.proof_waiting && <p className="mt-2 text-[16px]" data-testid="track-proof-line">Your proof is ready — approve it and the job prints. <Link to={`/proof/${view.proof_token}`} className="underline" data-testid="track-proof-link">See the proof</Link></p>}
            {view.balance_due > 0 && <p className="mt-2 text-[16px]" data-testid="track-balance-line">Balance due: {money(view.balance_due)}{view.balance_for ? ` for ${view.balance_for}` : ""}.</p>}
            <div className="mt-3"><Lines items={view.items || []} /></div>
            <p className="mt-2 flex justify-between text-[16px]"><span>Total</span><span className="font-semibold" data-testid="track-total">{money(view.total)}</span></p>
            {shop.phone && <p className="mt-2 text-[16px]" data-testid="track-text-shop">Questions? Text <a href={`sms:${tel(shop.phone)}`} className="underline">{shop.phone}</a></p>}
            {error && <p className="mt-3 text-[16px]" style={{ color: "var(--error)" }} data-testid="track-error">{error}</p>}
            {primary && (primary.to
                ? <Link to={primary.to} className={`${btnPrimary} mt-4`} data-testid={primary.testid}>{primary.label}</Link>
                : <button type="button" onClick={primary.onClick} disabled={busy || reordering} className={`${btnPrimary} mt-4`} data-testid={primary.testid}>{busy || reordering ? "Working…" : primary.label}</button>)}
            {undo > 0 && (
                <button type="button" onClick={undoCancel} className={`${btnPrimary} mt-4`} data-testid="track-undo">Undo cancel ({undo})</button>
            )}
            {view.can_cancel && !view.cancelled && (
                <button type="button" onClick={cancel} disabled={busy} className="mt-3 flex min-h-[44px] w-full items-center justify-center text-[16px] underline" data-testid="track-cancel">Cancel this order</button>
            )}
        </article>
    );
}
